/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getImageUrl } from '../../../api';

const LessonsPreview = ({ fragments: propFragments }) => {
    const location = useLocation();
    const [fragments, setFragments] = useState(propFragments || location.state?.fragments || []);

    useEffect(() => {
        if (!propFragments && !location.state?.fragments) {
            const storedFragments = localStorage.getItem('previewFragments');
            if (storedFragments) {
                try {
                    setFragments(JSON.parse(storedFragments));
                } catch (e) {}
            }
        } else {
            setFragments(propFragments || location.state?.fragments || []);
        }
    }, [propFragments, location.state]);

    const [resolvedImages, setResolvedImages] = useState({});

    useEffect(() => {
        const fetchImages = async () => {
            const newResolved = { ...resolvedImages };
            let changed = false;

            for (const fragment of fragments) {
                if (fragment.image_list) {
                    for (const imgId of fragment.image_list) {
                        if (imgId && !newResolved[imgId]) {
                            try {
                                const url = await getImageUrl(imgId);
                                newResolved[imgId] = url;
                                changed = true;
                            } catch (e) {
                                console.error('Failed to fetch image url', e);
                            }
                        }
                    }
                }
            }

            if (changed) {
                setResolvedImages(newResolved);
            }
        };

        if (fragments.length > 0) {
            fetchImages();
        }
    }, [fragments]);
    return (
        <div style={styles.container}>
            <div style={styles.mobileFrame}>
                <div style={styles.header}>
                    <h2 style={styles.headerTitle}>Lesson Preview</h2>
                </div>
                <div style={styles.scrollArea}>
                    {fragments.length === 0 ? (
                        <p style={styles.emptyText}>No fragments to display.</p>
                    ) : (
                        fragments.map((fragment, index) => {
                            let contentWithImages = fragment.content || '';
                            const usedImageIndices = new Set();

                            if (contentWithImages) {
                                const regex = /\[IMAGE_(\d+)\]/g;
                                contentWithImages = contentWithImages.replace(regex, (match, p1) => {
                                    const imageIndex = parseInt(p1, 10);
                                    if (fragment.image_list && fragment.image_list[imageIndex]) {
                                        const fileId = fragment.image_list[imageIndex];
                                        usedImageIndices.add(imageIndex);
                                        const url = resolvedImages[fileId];
                                        if (url) {
                                            return `<img src="${url}" alt="fragment-image-${imageIndex}" style="width: 100%; border-radius: 8px; object-fit: cover; margin: 12px 0; display: block;" />`;
                                        }
                                        return `<div style="width: 100%; min-height: 100px; background-color: #e5e7eb; border-radius: 8px; margin: 12px 0; display: flex; align-items: center; justify-content: center; color: #6b7280; font-size: 14px;">Loading Image...</div>`;
                                    }
                                    return match;
                                });
                            }

                            const remainingImages = (fragment.image_list || []).map((img, idx) => ({ img, idx })).filter(({ img, idx }) => img && !usedImageIndices.has(idx));

                            return (
                                <div key={index} style={styles.fragmentCard}>
                                    {fragment.title && <h3 style={styles.fragmentTitle}>{fragment.title}</h3>}
                                    <div style={styles.fragmentTypeBadge}>{fragment.type || 'text'}</div>
                                    <div
                                        style={styles.fragmentContent}
                                        dangerouslySetInnerHTML={{ __html: contentWithImages }}
                                    />
                                    {(fragment.type === 'test' || fragment.type === 'true_or_false' || fragment.type === 'finish_sentence') && fragment.answers && fragment.answers.length > 0 && (
                                        <div style={styles.answersContainer}>
                                            {fragment.answers.map((answer, ansIdx) => (
                                                <div
                                                    key={ansIdx}
                                                    style={{
                                                        ...styles.answerOption,
                                                        ...(answer.isRight ? styles.answerRight : {})
                                                    }}
                                                >
                                                    <span style={styles.answerText}>{answer.text}</span>
                                                    {answer.isRight && <span style={styles.answerIcon}>✓</span>}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {fragment.type === 'match_pairs' && fragment.words && fragment.translations && (
                                        <div style={styles.matchPairsContainer}>
                                            {fragment.words.map((wordObj, wpIdx) => {
                                                const translationObj = fragment.translations.find(t => t.position === wordObj.position);
                                                return (
                                                    <div key={wpIdx} style={styles.matchPairRow}>
                                                        <div style={styles.matchPairWord}>{wordObj.word}</div>
                                                        <div style={styles.matchPairDivider}>-</div>
                                                        <div style={styles.matchPairTranslation}>{translationObj ? translationObj.translation : '???'}</div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                    {remainingImages.length > 0 && (
                                        <div style={styles.imageGallery}>
                                            {remainingImages.map(({ img: fileId, idx }) => (
                                                resolvedImages[fileId] ?
                                                    <img key={idx} src={resolvedImages[fileId]} alt={`fragment-${index}-img-${idx}`} style={styles.image} />
                                                    : <div key={idx} style={{ ...styles.image, backgroundColor: '#e5e7eb', minHeight: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280', fontSize: '14px' }}>Loading...</div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};

const styles = {
    container: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '40px 20px',
        backgroundColor: '#e5eff8', // matching other pages background roughly
        minHeight: '100vh',
    },
    mobileFrame: {
        width: '100%',
        maxWidth: '390px', // Typical mobile width
        height: '844px', // Typical mobile height
        backgroundColor: '#ffffff',
        borderRadius: '40px',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        border: '8px solid #1f2937', // "Bezel"
    },
    header: {
        backgroundColor: '#ffffff',
        padding: '20px 20px 10px 20px',
        borderBottom: '1px solid #e5e7eb',
        textAlign: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 10,
    },
    headerTitle: {
        margin: 0,
        fontSize: '18px',
        fontWeight: '600',
        color: '#3c5a5d',
    },
    scrollArea: {
        flex: 1,
        overflowY: 'auto',
        padding: '20px',
        backgroundColor: '#f9fafb',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    emptyText: {
        textAlign: 'center',
        color: '#6b7280',
        marginTop: '40px',
    },
    fragmentCard: {
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        padding: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #f3f4f6',
    },
    fragmentTitle: {
        margin: '0 0 8px 0',
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#1f2937',
    },
    fragmentTypeBadge: {
        display: 'inline-block',
        backgroundColor: '#e0e7ff',
        color: '#4338ca',
        fontSize: '12px',
        fontWeight: '600',
        padding: '4px 8px',
        borderRadius: '12px',
        marginBottom: '12px',
        textTransform: 'uppercase',
    },
    fragmentContent: {
        fontSize: '15px',
        color: '#374151',
        lineHeight: '1.5',
    },
    imageGallery: {
        marginTop: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    image: {
        width: '100%',
        borderRadius: '8px',
        objectFit: 'cover',
    },
    answersContainer: {
        marginTop: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    answerOption: {
        padding: '12px 16px',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    answerRight: {
        border: '1px solid #10b981',
        backgroundColor: '#ecfdf5',
    },
    answerText: {
        fontSize: '15px',
        color: '#374151',
    },
    answerIcon: {
        color: '#10b981',
        fontWeight: 'bold',
    },
    matchPairsContainer: {
        marginTop: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    matchPairRow: {
        display: 'flex',
        alignItems: 'center',
        padding: '12px 16px',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb',
        gap: '12px',
    },
    matchPairWord: {
        flex: 1,
        fontSize: '15px',
        fontWeight: '600',
        color: '#4f46e5',
        textAlign: 'right',
    },
    matchPairDivider: {
        color: '#9ca3af',
        fontWeight: 'bold',
    },
    matchPairTranslation: {
        flex: 1,
        fontSize: '15px',
        color: '#374151',
    }
};

export default LessonsPreview;
